import { Link } from "react-router-dom";
import { Award, ChevronRight, Facebook, Instagram, Linkedin, Mail, MapPin, Phone } from "lucide-react";
import { EDX } from "@/lib/api";
import "./Footer.css";

const TikTokIcon = ({ size = 19 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size} aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.36a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.79z" />
  </svg>
);

const exploreLinks = [
  ["Services", "/services"], ["Pricing", "/pricing"], ["Case Studies", "/case-studies"],
  ["About", "/about"], ["Insights", "/blog"], ["Careers", "/careers"],
] as const;
const companyLinks = [["Book Consult", "/book"], ["Contact", "/contact"], ["FAQ", "/faq"], ["Admin", "/admin"]] as const;

function FooterLinks({ links }: { links: ReadonlyArray<readonly [string, string]> }) {
  return <ul className="edx-footer-links">{links.map(([label, to]) => (
    <li key={label}><Link to={to}><span>{label}</span><ChevronRight aria-hidden="true" /></Link></li>
  ))}</ul>;
}

export default function Footer() {
  return (
    <footer className="edx-footer" data-testid="footer">
      <div className="edx-footer-orb edx-footer-orb-left" aria-hidden="true" />
      <div className="edx-footer-orb edx-footer-orb-right" aria-hidden="true" />
      <div className="edx-footer-star edx-footer-star-one" aria-hidden="true" />
      <div className="edx-footer-star edx-footer-star-two" aria-hidden="true" />
      <div className="edx-footer-wave" aria-hidden="true" />

      <div className="edx-footer-main">
        <section className="edx-footer-brand" aria-label="About EDENOX">
          <Link to="/" className="edx-footer-brand-lockup" aria-label="EDENOX home">
            <img src={EDX.logo} alt="" />
            <span><b>EDENOX</b><small>INNOVATION, FORTIFIED!</small></span>
          </Link>
          <p>
            Built in Johannesburg. Engineered for the next 30 years.<br />
            EDENOX delivers software, AI, and digital transformation<br className="edx-footer-desktop-break" />
            for the world&apos;s most ambitious teams.
          </p>
          <div className="edx-footer-credential">
            <Award aria-hidden="true" /><span>B-BBEE LEVEL 1</span><i /><span>135% PROCUREMENT</span>
          </div>
          <div className="edx-footer-socials" aria-label="EDENOX social links">
            <a href={`mailto:${EDX.emails.info}`} aria-label="Email EDENOX"><Mail /></a>
            <a href={EDX.socials.instagram} target="_blank" rel="noreferrer" aria-label="Instagram"><Instagram /></a>
            <a href={EDX.socials.tiktok} target="_blank" rel="noreferrer" aria-label="TikTok"><TikTokIcon /></a>
            <a href={EDX.socials.facebook} target="_blank" rel="noreferrer" aria-label="Facebook"><Facebook /></a>
            <a href={EDX.socials.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn"><Linkedin /></a>
          </div>
        </section>

        <section className="edx-footer-panel edx-footer-contact" aria-labelledby="footer-contact-heading">
          <div className="edx-footer-heading-row"><h2 id="footer-contact-heading">Get in <span>touch</span></h2><i className="edx-footer-signal" aria-hidden="true" /></div>
          <div className="edx-footer-heading-line" aria-hidden="true" />
          <address>
            <div><i className="tone-cyan"><MapPin /></i><span>{EDX.location}</span></div>
            <div><i className="tone-cyan"><Mail /></i><a href={`mailto:${EDX.emails.info}`}>{EDX.emails.info}</a></div>
            <div><i className="tone-pink"><Mail /></i><a href={`mailto:${EDX.emails.contact}`}>{EDX.emails.contact}</a></div>
            <div><i className="tone-orange"><Mail /></i><a href={`mailto:${EDX.emails.billing}`}>{EDX.emails.billing}</a></div>
            <div><i className="tone-green"><Phone /></i><a href="tel:+27693974968">+27 69 397 4968</a></div>
          </address>
        </section>

        <section className="edx-footer-panel edx-footer-nav" aria-labelledby="footer-explore-heading">
          <h2 id="footer-explore-heading">Explore</h2><div className="edx-footer-heading-line" aria-hidden="true" /><FooterLinks links={exploreLinks} />
        </section>

        <section className="edx-footer-panel edx-footer-nav edx-footer-company" aria-labelledby="footer-company-heading">
          <h2 id="footer-company-heading">Company</h2><div className="edx-footer-heading-line tone-purple" aria-hidden="true" /><FooterLinks links={companyLinks} />
          <div className="edx-footer-motto"><i /><span>BOLDER<br />SOLUTIONS<br />BRIGHTER<br />TOMORROWS</span></div>
        </section>
      </div>

      <div className="edx-footer-rally" aria-hidden="true"><span>PEOPLE&nbsp; + &nbsp;TECHNOLOGY&nbsp; + &nbsp;A BRIGHTER TOMORROW</span><i /></div>
      <div className="edx-footer-bottom">
        <span>© 2026 EDENOX. All rights reserved.</span>
        <span><i /> BUILT IN SOUTH AFRICA. &nbsp; GLOBAL BY NATURE.</span>
      </div>
    </footer>
  );
}
